import React from "react";
import { Paper, Typography, Skeleton, Tooltip, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Button from "@mui/material/Button";
import { SYNC_RESULTS_TEXTS, UNAVAILABLE_SYNC_STATES } from "../consts";
import { useSalesforceIntegrationDetailsContext } from "../contexts/useSalesforceIntegrationDetailsContext";
import { EnrichmentSynchronizationState } from "../types";

const formatDate = (
  dateValue: Date | string | null,
  synchronizationState?: EnrichmentSynchronizationState
) => {
  if (!dateValue) {
    // Check if sync is in progress but no syncTime yet (first sync)
    const isSyncing =
      synchronizationState &&
      UNAVAILABLE_SYNC_STATES.includes(synchronizationState);
    if (isSyncing) return SYNC_RESULTS_TEXTS.dateStates.syncing;
    return SYNC_RESULTS_TEXTS.dateStates.never;
  }

  try {
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    return date.toLocaleString();
  } catch {
    return SYNC_RESULTS_TEXTS.dateStates.invalid;
  }
};

export const SyncResults: React.FC = () => {
  const {
    syncResult,
    syncResultLoading,
    generatingReport,
    handleGenerateSyncErrorsReport,
    synchronizationState,
  } = useSalesforceIntegrationDetailsContext();

  const loading = syncResultLoading || generatingReport;

  // Show "-" when syncResult is undefined, but preserve actual values (including 0) when it exists
  const total = syncResult ? syncResult.totalRecords || 0 : "-";
  const matches = syncResult ? syncResult.matchedRecords || 0 : "-";
  const notFound = syncResult ? syncResult.notMatchedRecords || 0 : "-";
  const date = syncResult?.syncTime || null;
  const hasSyncErrors = syncResult?.hasErrors || false;

  const ResultItem = ({
    label,
    value,
    color = "text.primary",
    hint,
  }: {
    label: string;
    value: number | string;
    color?: string;
    hint?: string;
  }) => (
    <Stack spacing={0.5}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        {hint && (
          <Tooltip title={hint} arrow>
            <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          </Tooltip>
        )}
      </Stack>
      {loading ? (
        <Skeleton width={60} height={24} />
      ) : (
        <Typography variant="h6" color={color}>
          {typeof value === "number" ? value.toLocaleString() : value}
        </Typography>
      )}
    </Stack>
  );

  return (
    <Paper
      data-testid="salesforce-sync-results-section"
      elevation={0}
      sx={{ p: 3, border: 1, borderColor: "divider" }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h6">{SYNC_RESULTS_TEXTS.title}</Typography>
        {hasSyncErrors && handleGenerateSyncErrorsReport && (
          <Button
            data-testid="salesforce-sync-errors-report-btn"
            variant="outlined"
            size="small"
            color="error"
            startIcon={<ErrorOutlineIcon />}
            onClick={handleGenerateSyncErrorsReport}
          >
            {SYNC_RESULTS_TEXTS.buttonText}
          </Button>
        )}
      </Stack>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <ResultItem
            label={SYNC_RESULTS_TEXTS.labels.total}
            value={total}
            hint={SYNC_RESULTS_TEXTS.hints.total}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <ResultItem
            label={SYNC_RESULTS_TEXTS.labels.matches}
            value={matches}
            color="success.main"
            hint={SYNC_RESULTS_TEXTS.hints.matched}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <ResultItem
            label={SYNC_RESULTS_TEXTS.labels.notFound}
            value={notFound}
            color="error.main"
            hint={SYNC_RESULTS_TEXTS.hints.notMatched}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <ResultItem
            label={SYNC_RESULTS_TEXTS.labels.lastUpdated}
            value={formatDate(date, synchronizationState)}
            color="text.secondary"
          />
        </Grid>
      </Grid>
    </Paper>
  );
};
